<template>

<!--
	The card's guess distribution: a compact, full-bleed bar strip anchored to the
	card's bottom border (no axis/labels — the archive card has no room for them).
	One bar per guess bucket (1..16) plus a loss bar. It mirrors the end-screen
	"Tiles to solve" chart's colours: neutral bars, the player's own solved bucket
	green (--correct), the loss bar red (--wrong). Heights are relative to the
	busiest bucket; purely decorative, so it's aria-hidden.
-->

<div class="archive-dist" aria-hidden="true">
	<span
		v-for="bar in bars"
		:key="bar.key"
		class="archive-dist-col"
		:class="{ 'is-self': bar.self, 'is-loss': bar.loss }"
	>
		<span class="archive-dist-bar" :style="{ height: barHeight(bar.count) }"></span>
	</span>
</div>

</template>

<script>

var MAX_GUESSES = 16

export default {

	name: 'ArchiveDistribution',

	props: {
		// the per-bucket counts { '1'..'16': n, loss: n } from day.metrics.dist
		dist: {
			default: null
		},
		// the player's own guess count for this day (their bucket goes green when
		// they solved); null when they have no live result for the day
		selfGuesses: {
			default: null
		},
		// whether the player's own result was a solve (a loss highlights the loss
		// bar red instead of greening a numbered bucket)
		selfSolved: {
			type: Boolean,
			default: false
		}
	},

	computed: {

		// ordered bars: buckets 1..16 then loss, each flagged self/loss for colour
		bars: function() {

			var d = this.dist || {}
			var rows = []

			var ownKey = (this.selfSolved && this.selfGuesses) ? String(this.selfGuesses) : null

			for (var g = 1; g <= MAX_GUESSES; g++) {

				var key = String(g)

				rows.push({
					key: key,
					count: d[key] || 0,
					loss: false,
					self: key === ownKey
				})

			}

			rows.push({
				key: 'loss',
				count: d.loss || 0,
				loss: true,
				self: this.selfGuesses != null && !this.selfSolved
			})

			return rows

		},

		maxCount: function() {

			var max = 0

			this.bars.forEach(function(row) {

				if (row.count > max) max = row.count

			})

			return max

		}

	},

	methods: {

		// height relative to the busiest bucket (min 8% so any non-zero count
		// still reads as a bar); 0 for an empty bucket
		barHeight: function(count) {

			if (!this.maxCount || !count) return '0%'

			var pct = (count / this.maxCount) * 100

			return Math.max(8, pct) + '%'

		}

	}

}

</script>

<style scoped>

/* matches the end-screen "Tiles to solve" chart: thin accent bars with rounded
   tops. Anchored flush to the card's bottom border; capped + centred so the bars
   don't stretch wide and sparse on a wide card. */
.archive-dist {
	display: flex;
	align-items: flex-end;
	gap: 3px;
	width: 100%;
	max-width: 300px;
	margin: 0 auto;
	height: 36px;
}

.archive-dist-col {
	flex: 1;
	min-width: 0;
	height: 100%;
	display: flex;
	align-items: flex-end;
	justify-content: center;
}

/* muted bars so the player's own (green) bucket stands out as their position */
.archive-dist-bar {
	width: 74%;
	background: #adb4bf;
	border-radius: 2px 2px 0 0;
	transition: height 320ms ease;
}

/* the player's own solved bucket — kept full green so they spot their position */
.archive-dist-col.is-self .archive-dist-bar {
	background: var(--correct);
}

/* losses — muted red */
.archive-dist-col.is-loss .archive-dist-bar {
	background: #b2938d;
}

@media (prefers-reduced-motion: reduce) {

	.archive-dist-bar {
		transition: none;
	}

}

</style>
